import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';

import Text from '@lightspeed/cirrus-text';
import { IconClock } from '@lightspeed/flame/Icon/Clock';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { IconSmallChevronDown } from '@lightspeed/flame/Icon/SmallChevronDown';
import { Popover } from '@lightspeed/flame/Popover';

import { changeTime, DEFAULT_TIME_FORMAT, MIDNIGHT } from 'pages/utils/time';
import TimeRangePickerForm from './TimeRangePickerForm';

const START_TIME = 'startTime';
const END_TIME = 'endTime';

class TimeRangePicker extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      focusedInput: START_TIME,
    };

    this.renderButton = this.renderButton.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleStartTimeFocus = this.handleStartTimeFocus.bind(this);
    this.handleEndTimeFocus = this.handleEndTimeFocus.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  get startTime() {
    const { startDate, timeFormat } = this.props;

    return moment(startDate).format(timeFormat);
  }

  get endTime() {
    const { endDate, timeFormat } = this.props;

    return moment(endDate).format(timeFormat);
  }

  get allDay() {
    const { startDate, endDate } = this.props;
    const isAllDay =
      moment(startDate).format(DEFAULT_TIME_FORMAT) ===
        moment(MIDNIGHT).format(DEFAULT_TIME_FORMAT) &&
      moment(endDate).format(DEFAULT_TIME_FORMAT) === moment(MIDNIGHT).format(DEFAULT_TIME_FORMAT);
    return isAllDay.toString();
  }

  handleButtonClick() {
    this.setState(() => ({
      isOpen: true,
      focusedInput: START_TIME,
    }));
  }

  handleClose() {
    this.setState(() => ({
      isOpen: false,
    }));
  }

  handleSubmit(values) {
    const { startDate, endDate, timeFormat, onChange } = this.props;

    onChange({
      startDate: changeTime({
        oldDate: startDate,
        newDate: moment(values[START_TIME], timeFormat),
      }),
      endDate: changeTime({ oldDate: endDate, newDate: moment(values[END_TIME], timeFormat) }),
    });
    this.handleClose();
  }

  handleStartTimeFocus() {
    this.setState(() => ({
      focusedInput: START_TIME,
    }));
  }

  handleEndTimeFocus() {
    this.setState(() => ({
      focusedInput: END_TIME,
    }));
  }

  handleError(errors) {
    this.setState(() => ({
      // eslint-disable-next-line react/no-unused-state
      errors,
    }));
  }

  renderButton({ targetProps, targetEvents, active }) {
    const { startDate, endDate, timeFormat } = this.props;
    const buttonCls = cn('cr-button', 'time-picker__button', {
      'time-picker__button--active': active,
    });

    return (
      <button
        type="button"
        className={buttonCls}
        {...targetProps}
        {...targetEvents}
        onClick={() => {
          this.handleButtonClick();
        }}
      >
        <div key="clock" className="cr-button__content">
          <IconClock />
        </div>
        <div key="start-date" className="cr-button__content">
          {startDate && <Text dimmed>{startDate.format(timeFormat)}</Text>}
        </div>
        <div key="arrow" className="cr-button__content">
          <IconArrowRight />
        </div>
        <div key="end-date" className="cr-button__content">
          {endDate && <Text dimmed>{endDate.format(timeFormat)}</Text>}
        </div>
        <div key="arrow-down" className="cr-button__content">
          <IconSmallChevronDown />
        </div>
      </button>
    );
  }

  render() {
    const { timeFormat } = this.props;
    const { focusedInput, isOpen } = this.state;
    return (
      <Popover
        light
        target={this.renderButton}
        isOpen={isOpen}
        onClose={this.handleClose}
        zIndex={1}
      >
        <div className="cr-time-picker cr-flex cr-flex-col">
          <TimeRangePickerForm
            startTime={this.startTime}
            endTime={this.endTime}
            allDay={this.allDay}
            focusedInput={focusedInput}
            timeFormat={timeFormat}
            onStartTimeFocus={this.handleStartTimeFocus}
            onEndTimeFocus={this.handleEndTimeFocus}
            onSubmit={this.handleSubmit}
          />
        </div>
      </Popover>
    );
  }
}

TimeRangePicker.propTypes = {
  timeFormat: PropTypes.string,
  startDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }).isRequired,
  endDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

TimeRangePicker.defaultProps = {
  timeFormat: DEFAULT_TIME_FORMAT,
};

export default TimeRangePicker;

export { START_TIME, END_TIME };
